import { z } from 'zod';

/**
 * Tag conosciuti dell'equipaggiamento (manuale V1-20260507, rr. 1457-1546).
 *
 * Lista NON bloccante: il campo `tags` di {@link ItemSchema} accetta qualunque
 * stringa per lasciare spazio a equipaggiamenti homebrew. Questi sono i valori
 * standard da usare per autocomplete, lookup nella UI e dati delle classi.
 *
 * Le tre macro-sezioni rispecchiano la struttura del manuale V1:
 *  - **Generali**: si applicano a qualsiasi oggetto (rr. 1457-1489)
 *  - **Armi**: tag specifici offensivi (rr. 1496-1520)
 *  - **Armature**: tag specifici difensivi (rr. 1538-1546)
 *
 * **Tag parametrizzati** (`+n armatura contro x`, `immune a x`, …): le stringhe
 * qui sono **template letterali** del manuale. Nei dati reali si sostituiscono
 * `n` (numero) e `x` (parola), es. `'+1 armatura contro fuoco'`. Quando ci
 * sarà l'autocomplete UI, separeremo "tag fissi" da "tag template" in due
 * costanti distinte.
 *
 * **Etichetta speciale** `[lorem]` (r. 1489): jolly narrativo — applica il
 * "significato naturale" del tag nella fiction. Non ha regole codificate.
 *
 * **Meta-tag numerici NON inclusi**: `peso n`, `n monete`, `n utilizzi`,
 * `n munizioni`, `+n danni`, `n perforante`, `armatura n`, `+n armatura`.
 * Sono già modellati come campi separati di {@link ItemSchema}
 * (`weight`, `uses`, `armor`, `damage`, `armorPiercing`, …).
 */
export const KNOWN_EQUIPMENT_TAGS = [
  // === Etichette Generali (manuale V1, rr. 1457-1489) ===
  // portata
  'corta',
  'media',
  'lunga',
  'vicino',
  'lontano',
  // applicazione / modalità d'uso
  'a contatto',
  'applicato',
  'lento',
  'pericoloso',
  'scomodo',
  'richiede',
  // resistenze (template parametrizzati)
  'immune a x',
  'resistente a x',
  'vulnerabile a x',
  // universale narrativa
  '[lorem]',
  // forma / impugnatura
  'a due mani',
  'ingombrante',
  // tipo / consumo
  'indossato',
  'razione',
  'ricarica',

  // === Etichette delle Armi (manuale V1, rr. 1496-1520) ===
  'brutale',
  'precisa',
  'smussata',
  'stordente',
  'implacabile',
  'letale',
  'innocua',
  'perforante',
  'ignora armatura',
  'impatto',
  'da lancio',

  // === Etichette delle Armature (manuale V1, rr. 1538-1546) ===
  '+n armatura contro x',
  'rende immune a x',
  'rende resistente a x',
] as const;

/**
 * Schema di un singolo oggetto di equipaggiamento.
 *
 * Include slot opzionali per i numeri "tipici" (peso, danno, armatura,
 * utilizzi, perforante) e una lista libera di `tags` per le proprietà
 * categoriche. Gli effetti narrativi unici vanno in `description`.
 */
export const ItemSchema = z.object({
  /** ID stabile (es. 'pugnale', 'maglia-di-ferro'). */
  id: z.string().min(1),
  name: z.string().min(1).max(100),
  /**
   * Peso UNITARIO in unità di Carico. 0 = trasportato senza penalità.
   *
   * **Semantica unitaria**: `weight` è il peso del singolo pezzo, NON dello
   * stack. Il peso totale di uno stack di N pezzi è `weight × quantity` —
   * vedi {@link getItemTotalWeight} in `src/utils/`.
   */
  weight: z.number().min(0).default(0),
  /**
   * Numero di pezzi identici impilati in questo entry dell'inventario.
   * **Opzionale**: se assente, semanticamente equivale a 1 (item singolo).
   * Vedi {@link getItemTotalWeight} per il consumo idiomatico (`?? 1`).
   *
   * Esempi tipici dal manuale V1:
   *  - 10 monete iniziali (Ladro): `quantity: 10`
   *  - 3 Pugnali da lancio: `quantity: 3`
   *  - 2 Pozioni Curative: `quantity: 2`
   *
   * **Distinzione da `uses`**: `quantity` conta i pezzi distinti; `uses`
   * conta le cariche residue di un singolo pezzo a carica (es. una
   * pozione potrebbe avere `quantity: 1, uses: 5` se fosse a 5 cariche).
   * I due campi non interagiscono.
   *
   * **Optional vs default**: il campo è `optional` (non `default(1)`) per
   * mantenere additivo non-breaking l'introduzione (FI-16): i dati esistenti
   * che non lo dichiarano restano validi a livello TS senza modifiche.
   *
   * Risolve **FI-16** (modellazione strutturata di item quantitati).
   */
  quantity: z.number().int().positive().optional(),
  /**
   * Modificatore di danno per le armi (stringa libera).
   * Esempi: "+1 danno", "1d4 fuoco", "metà danno".
   * Se assente, l'arma usa il dado base della classe del PG.
   */
  damage: z.string().optional(),
  /** Valore Armatura conferito (per armature/scudi). */
  armor: z.number().int().min(0).optional(),
  /** Utilizzi residui (per consumabili / oggetti a carica). */
  uses: z.number().int().min(0).optional(),
  /**
   * Penetrazione armatura.
   * - undefined: non perforante
   * - 0: perforante "base"
   * - 1+: +N perforante
   */
  armorPiercing: z.number().int().min(0).optional(),
  /**
   * Tag categorici (es. 'corta', 'precisa', 'a due mani', 'indossato').
   * Stringhe arbitrarie: vedere {@link KNOWN_EQUIPMENT_TAGS} per i valori
   * standard usati dalla UI per autocomplete.
   */
  tags: z.array(z.string().min(1)).default([]),
  /** Descrizione narrativa libera (effetti unici, lore, restrizioni). */
  description: z.string().optional(),
  /**
   * **FI-50 (2026-05-22)** — testo narrativo inserito dal giocatore in
   * fase di creazione PG. Vuoto/assente per item non personalizzati.
   * Esempi tipici: descrizione del Ricordo Barbaro, del Simbolo sacro
   * Chierico/Druido/Paladino, dello Strumento del Bardo.
   *
   * **Distinzione da `description`**: `description` viene dal catalogo
   * (immutabile, narrativa autoriale); `userDescription` è proprietà
   * del PG (mutabile, narrativa del giocatore).
   */
  userDescription: z.string().max(500).optional(),
  /**
   * **FI-50** — prompt/hint dal catalogo che invita il giocatore a
   * compilare {@link userDescription}. Vive solo nei `src/data/classes/*.ts`
   * per gli starting item che lo richiedono. La UI builder lo mostra come
   * placeholder/label della textarea inline.
   */
  userDescriptionPrompt: z.string().optional(),
});
export type Item = z.infer<typeof ItemSchema>;
